import { Injectable, InjectionToken, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BuildEnvironment } from 'src/environments/environment.types';

type InjectionWindow = Window & typeof globalThis;

/**
 * This is an artifact of writing tests and having trouble mocking the window. Leaving in for now.
 */
export const WINDOW: InjectionToken<InjectionWindow> =
  new InjectionToken<InjectionWindow>('Global window object', {
    factory: () => window,
  });

const FLAG_PREFIX = 'flag';

@Injectable()
export class FeatureFlagService {
  private _isReady = false;
  private _flags: Set<string> = new Set();
  public window: (Window & typeof globalThis) | null;
  constructor() {
    this.window = inject(WINDOW);
  }

  get flags(): Set<string> {
    return this._flags;
  }

  private init(): void {
    for (const key in this.getEnv()) {
      if (
        key.startsWith(FLAG_PREFIX) &&
        this.getEnv()[key as keyof BuildEnvironment]
      ) {
        this._flags.add(key);
      }
    }

    // In non-production environments override flag settings by adding flags to localStorage.
    if (this.getEnv().platform !== 'Production') {
      const testingKeys = this.window?.localStorage.getItem('flags');
      if (testingKeys) {
        testingKeys.split(',').forEach((flag) => {
          const flagClean = flag.trim();
          // allow turning off flags in dev for testing.
          if (flagClean.startsWith('!')) {
            const subFlag = flagClean.substring(1);
            if (this._flags.has(subFlag)) {
              this._flags.delete(subFlag);
            }
          } else {
            this._flags.add(flagClean);
          }
        });
      }
    }

    this._isReady = true;
  }

  public isActive(flag: string): boolean {
    // Don't initialize in construction because it makes it hard to test
    if (!this._isReady) {
      this.init();
    }
    return this._flags.has(flag);
  }

  // This method allows us to override the environment in testing.
  public getEnv(): Partial<BuildEnvironment> {
    return environment;
  }
}
